import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePaystackPayment } from 'react-paystack';
import { toast } from 'react-hot-toast';
import { subscriptionService, SubscriptionUsage } from '../../services/subscriptionService';
import { FREEMIUM_LIMITS } from '../../types';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  usage?: SubscriptionUsage | null;
  onUpgradeSuccess?: () => void;
}

export const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  usage,
  onUpgradeSuccess,
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'paystack' | 'bank'>('paystack');

  const isOrg = Boolean(usage?.isOrganization || user?.organizationId);
  const currentCount = usage?.currentCount ?? 0;
  const limit = usage?.limit ?? (isOrg ? FREEMIUM_LIMITS.ORGANIZATION : FREEMIUM_LIMITS.INDIVIDUAL);

  // Pricing configuration
  const INDIVIDUAL_PRICE_NGN = 5000;
  const ORG_PRICE_NGN = 50000;
  const priceNgn = isOrg ? ORG_PRICE_NGN : INDIVIDUAL_PRICE_NGN;

  const PAYSTACK_PUBLIC_KEY = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || 'pk_live_17b9284e8e4e4333a1ab0479130f01a63ac29a60';

  const paystackConfig = {
    reference: `VRS-SUB-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    email: user?.email || 'customer@verisure.online',
    amount: priceNgn * 100, // Paystack in kobo
    publicKey: PAYSTACK_PUBLIC_KEY,
  };

  const initializePayment = usePaystackPayment(paystackConfig);

  const handlePaystackSuccess = async (reference: any) => {
    setLoading(true);
    toast.loading('Confirming premium subscription activation...');
    try {
      if (isOrg && user?.organizationId) {
        await subscriptionService.upgradeOrgSubscription(user.organizationId, 'premium');
      } else if (user?.id) {
        await subscriptionService.upgradeUserSubscription(user.id, 'premium');
      }
      toast.dismiss();
      toast.success('🎉 Welcome to Verisure Premium! Unlimited QR Codes Unlocked!', {
        duration: 5000,
      });
      if (onUpgradeSuccess) onUpgradeSuccess();
      onClose();
    } catch (err: any) {
      toast.dismiss();
      toast.error('Failed to activate subscription: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePaystackClose = () => {
    toast('Payment cancelled.', { icon: 'ℹ️' });
  };

  const handleTriggerPayment = () => {
    initializePayment({
      onSuccess: handlePaystackSuccess,
      onClose: handlePaystackClose,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md animate-in fade-in duration-200">
      <div className="relative w-full max-w-xl bg-slate-900 border border-orange-500/30 rounded-3xl shadow-2xl overflow-hidden text-slate-100 flex flex-col max-h-[92vh]">
        {/* Header Glow */}
        <div className="bg-gradient-to-r from-red-600/30 via-orange-500/20 to-amber-500/30 border-b border-orange-500/20 p-6 md:p-8 relative">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 text-slate-400 hover:text-white p-2 rounded-full hover:bg-white/10 transition"
          >
            ✕
          </button>

          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/20 border border-orange-500/40 text-orange-400 text-xs font-black tracking-wider uppercase mb-3">
            <span className="w-2 h-2 rounded-full bg-orange-400 animate-ping"></span>
            <span>FREEMIUM LIMIT REACHED</span>
          </div>

          <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight">
            Unlock Unlimited Hardware QR Codes
          </h2>
          <p className="text-slate-300 text-xs md:text-sm mt-1.5 leading-relaxed">
            {isOrg
              ? `Your organization has used all ${limit} free QR codes. Upgrade to Verisure Enterprise Premium for unlimited asset tracking and batch printing.`
              : `You have reached the individual free cap of ${limit} QR codes. Upgrade to Verisure Pro to register and manage unlimited personal equipment.`}
          </p>

          {/* Usage Meter Bar */}
          <div className="mt-4 bg-slate-950/60 rounded-2xl p-3 border border-white/10">
            <div className="flex justify-between items-center text-xs mb-1.5 font-bold">
              <span className="text-slate-400">Current Freemium Usage</span>
              <span className="text-orange-400 font-mono">
                {currentCount} / {limit} QR Codes (100% Used)
              </span>
            </div>
            <div className="w-full bg-slate-800 rounded-full h-2.5 overflow-hidden">
              <div className="bg-gradient-to-r from-orange-500 to-red-500 h-2.5 rounded-full w-full"></div>
            </div>
          </div>
        </div>

        {/* Content Body */}
        <div className="p-6 md:p-8 overflow-y-auto space-y-6 text-sm">
          {/* Plan Comparison Card */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Free Starter Tier */}
            <div className="bg-slate-950/60 border border-slate-800 rounded-2xl p-4 text-xs space-y-2.5 opacity-80">
              <div className="font-bold text-slate-400 uppercase tracking-wider text-[11px]">Free Starter</div>
              <div className="text-xl font-black text-white">₦0 <span className="text-xs font-normal text-slate-400">/ forever</span></div>
              <ul className="space-y-1.5 text-slate-300 text-[11px]">
                <li className="flex items-center gap-1.5 text-orange-400 font-bold">
                  <span>✓</span> Max {limit} QR Codes
                </li>
                <li className="flex items-center gap-1.5">
                  <span>✓</span> Digital Asset Passport
                </li>
                <li className="flex items-center gap-1.5">
                  <span>✓</span> Standard Scan Resolution
                </li>
                <li className="flex items-center gap-1.5 text-slate-500 line-through">
                  <span>✕</span> Unlimited QR Badges
                </li>
                <li className="flex items-center gap-1.5 text-slate-500 line-through">
                  <span>✕</span> Priority Insurance Evidence
                </li>
              </ul>
            </div>

            {/* Premium Tier */}
            <div className="bg-gradient-to-b from-orange-500/10 to-amber-500/5 border-2 border-orange-500/50 rounded-2xl p-4 text-xs space-y-2.5 relative shadow-lg shadow-orange-500/10">
              <span className="absolute -top-3 right-3 bg-gradient-to-r from-orange-500 to-red-500 text-white text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider shadow">
                Recommended
              </span>
              <div className="font-bold text-orange-400 uppercase tracking-wider text-[11px]">
                {isOrg ? 'Enterprise Premium' : 'Individual Pro'}
              </div>
              <div className="text-xl font-black text-white">
                ₦{priceNgn.toLocaleString()} <span className="text-xs font-normal text-slate-400">/ month</span>
              </div>
              <ul className="space-y-1.5 text-slate-200 text-[11px]">
                <li className="flex items-center gap-1.5 text-emerald-400 font-bold">
                  <span>★</span> Unlimited Hardware QR Codes
                </li>
                <li className="flex items-center gap-1.5 text-emerald-400 font-bold">
                  <span>★</span> High-Res Tamper-Evident Printing
                </li>
                <li className="flex items-center gap-1.5">
                  <span>✓</span> Resale Valuation Certificate
                </li>
                <li className="flex items-center gap-1.5">
                  <span>✓</span> Fast-Track Insurance Claim Audit
                </li>
                <li className="flex items-center gap-1.5">
                  <span>✓</span> Multi-user / Technician Tagging
                </li>
              </ul>
            </div>
          </div>

          {/* Payment Method Switcher */}
          <div className="space-y-3">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPaymentMethod('paystack')}
                className={`flex-1 py-2.5 px-3 rounded-xl font-bold text-xs border transition ${
                  paymentMethod === 'paystack'
                    ? 'bg-orange-500/20 border-orange-500 text-white'
                    : 'bg-slate-950/40 border-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                💳 Pay Online (Instant Card / USSD)
              </button>
              <button
                type="button"
                onClick={() => setPaymentMethod('bank')}
                className={`flex-1 py-2.5 px-3 rounded-xl font-bold text-xs border transition ${
                  paymentMethod === 'bank'
                    ? 'bg-orange-500/20 border-orange-500 text-white'
                    : 'bg-slate-950/40 border-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                🏦 Bank Transfer
              </button>
            </div>

            {paymentMethod === 'paystack' ? (
              <div className="bg-slate-950/60 border border-white/10 rounded-2xl p-4 text-xs text-slate-300 space-y-2">
                <p>
                  Instant activation via Paystack secure gateway. Your QR limit is lifted immediately after payment completes.
                </p>
                <button
                  type="button"
                  disabled={loading}
                  onClick={handleTriggerPayment}
                  className="w-full mt-2 py-3.5 bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 text-white font-black text-sm rounded-xl shadow-lg shadow-orange-500/25 transition flex items-center justify-center gap-2"
                >
                  <span>Pay ₦{priceNgn.toLocaleString()} &amp; Unlock Unlimited</span>
                  <span>&rarr;</span>
                </button>
              </div>
            ) : (
              <div className="bg-slate-950/60 border border-white/10 rounded-2xl p-4 text-xs text-slate-300 space-y-2">
                <div className="text-white font-bold">Verisure Operational Bank Account:</div>
                <div className="space-y-1 bg-slate-900/80 p-3 rounded-xl font-mono text-[11px] border border-white/5">
                  <div><strong className="text-slate-400 font-sans">Bank:</strong> Access Bank Plc</div>
                  <div><strong className="text-slate-400 font-sans">Account Name:</strong> Elemental Verisure Technologies</div>
                  <div><strong className="text-slate-400 font-sans">Account Number:</strong> <span className="text-orange-400 font-bold">0129384756</span></div>
                  <div><strong className="text-slate-400 font-sans">Payment Reference:</strong> <span className="text-emerald-400 font-bold">{user?.email || 'Your Email'}</span></div>
                </div>
                <p className="text-[11px] text-slate-400">
                  After transfer, send your receipt to <a href="mailto:billing@verisure.online" className="text-orange-400 underline">billing@verisure.online</a> or your platform admin for instant manual activation.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-white/10 bg-slate-950/70 flex justify-end gap-3 text-xs">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-slate-400 hover:text-white font-semibold transition"
          >
            Cancel / Close
          </button>
        </div>
      </div>
    </div>
  );
};
